import { ApiError, type MonthView, api } from './api.js'
import { esc } from './format.js'

/**
 * The edit dialog, and the sign-in panel that comes before everything.
 *
 * The prototype had no sign-in: it ran inside a Claude artifact. Here the app
 * is on the open internet, so the first thing a visitor meets is a password
 * and a 2FA code.
 */

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

// ------------------------------------------------------------------ sign-in

export function openSignIn(onSignedIn: () => void): void {
  document.body.innerHTML = `
    <main class="signin">
      <div class="signin-brand">
        <span class="brandmark"><img src="/ni-logo.png" alt="Network Intelligence"></span>
      </div>
      <h1>NI Money Manager</h1>
      <p class="sub">Monthly Cash Flow &amp; Spending Plan</p>
      <form id="signinForm" novalidate>
        <div class="field"><label for="si-email">Email</label>
          <input id="si-email" type="email" autocomplete="username" required></div>
        <div class="field"><label for="si-password">Password</label>
          <input id="si-password" type="password" autocomplete="current-password" required></div>
        <div class="field" id="si-codeField" hidden>
          <label for="si-code">6-digit code from your authenticator app</label>
          <input id="si-code" inputmode="numeric" autocomplete="one-time-code" maxlength="10">
          <p class="note">Lost your phone? <button type="button" class="linkish" id="si-useRecovery">Use a recovery code</button></p>
        </div>
        <p class="err" id="si-error" role="alert"></p>
        <button class="btn" type="submit" id="si-submit">Sign in</button>
      </form>
    </main>
    <style>
      .signin{max-width:32rem;margin:6vh auto;padding:1.5rem;font-family:var(--body,system-ui)}
      .signin-brand{display:flex;justify-content:center;margin-bottom:1.5rem}
      .signin-brand .brandmark{display:inline-flex;align-items:center;justify-content:center;
        background:#1e2a2c;border-radius:14px;padding:18px 22px;max-width:100%}
      /* Full size, shrinking only if the screen is narrower than the artwork. */
      .signin-brand img{display:block;width:100%;max-width:380px;height:auto}
      .signin h1{margin:0;font-family:var(--display,Georgia,serif)}
      .signin .sub{color:var(--muted,#5d6a69);margin:.25rem 0 1.5rem}
      .signin .field{margin-bottom:1rem;display:flex;flex-direction:column;gap:.35rem}
      .signin input{padding:.6rem;border:1px solid var(--line,#ccc);border-radius:.4rem;font:inherit}
      .signin .err{color:var(--bad,#b0443a);min-height:1.2em;margin:.5rem 0}
      .signin .note{font-size:.85em;color:var(--muted,#5d6a69)}
      .linkish{background:none;border:0;padding:0;color:var(--accent,#2f6b5f);text-decoration:underline;cursor:pointer;font:inherit}
    </style>`

  let stage: 'password' | 'code' | 'recovery' = 'password'

  const error = $('#si-error')
  const codeField = $('#si-codeField')
  const submit = $<HTMLButtonElement>('#si-submit')

  $('#si-useRecovery').addEventListener('click', () => {
    stage = 'recovery'
    $('#si-code').setAttribute('placeholder', 'XXXX-XXXX-XXXX')
    ;($('#si-code') as HTMLInputElement).value = ''
    error.textContent = ''
  })

  $('#signinForm').addEventListener('submit', (event) => {
    event.preventDefault()
    error.textContent = ''
    submit.disabled = true

    const finish = () => {
      submit.disabled = false
    }

    if (stage === 'password') {
      void api
        .post<{ next: 'code' | 'enrol' }>('/api/auth/login', {
          email: $<HTMLInputElement>('#si-email').value,
          password: $<HTMLInputElement>('#si-password').value,
        })
        .then((result) => {
          if (result.next === 'enrol') {
            error.textContent =
              'Your two-factor app is not set up yet. Ask an owner for a new setup link.'
            return
          }
          stage = 'code'
          codeField.hidden = false
          $<HTMLInputElement>('#si-code').focus()
          submit.textContent = 'Verify'
        })
        .catch((problem: unknown) => {
          error.textContent =
            problem instanceof ApiError ? problem.message : 'Could not sign in.'
        })
        .finally(finish)
      return
    }

    const url = stage === 'recovery' ? '/api/auth/recovery' : '/api/auth/mfa'
    void api
      .post(url, { code: $<HTMLInputElement>('#si-code').value })
      .then(() => {
        // The markup was replaced by the sign-in panel; reload to get it back.
        window.location.reload()
        onSignedIn()
      })
      .catch((problem: unknown) => {
        error.textContent = problem instanceof ApiError ? problem.message : 'Could not verify.'
      })
      .finally(finish)
  })
}

// ------------------------------------------------------------- edit dialog

type Store = 'bills' | 'payroll' | 'income' | 'categories' | 'entries'

/**
 * Resolves what a button asked for to a store name.
 *
 * The markup inherited from the prototype uses two vocabularies: the "+ Add"
 * buttons carry a singular form kind (`data-add="bill"`, `"report"`) while the
 * edit buttons carry a plural store name (`data-edit="bills:<id>"`). Both land
 * here, so both are accepted rather than one of them silently falling through
 * to the transaction form.
 */
const STORE_FOR: Readonly<Record<string, Store>> = {
  bill: 'bills',
  bills: 'bills',
  category: 'categories',
  categories: 'categories',
  payroll: 'payroll',
  income: 'income',
  entry: 'entries',
  entries: 'entries',
  // A reported purchase is a transaction that also carries a purchase report.
  report: 'entries',
}

interface Prefill {
  kind?: string
  occurredOn?: string
  revenueType?: string
  paymentMethod?: string
}

/**
 * Opens the add/edit dialog for one record.
 *
 * The dialog markup comes from the prototype; the fields shown depend on which
 * list the record belongs to.
 */
export function openEditor(
  requested: string,
  id: string,
  view: MonthView,
  onSaved: () => Promise<void>,
  prefill: Prefill = {},
): void {
  const store = STORE_FOR[requested]
  if (!store) {
    console.error(`No editor for "${requested}"`)
    return
  }

  const dialog = $<HTMLDialogElement>('#dlg')
  const form = $<HTMLFormElement>('#form')

  const existing = findExisting(store, id, view)
  // "+ Report a purchase" is a spending entry that starts with its report
  // fields showing.
  const startWith: Prefill = requested === 'report' ? { kind: 'expense', ...prefill } : prefill

  // The form is rendered first: #ferr lives inside it, so querying before this
  // point finds nothing. That is what stopped every one of these buttons.
  form.innerHTML = fieldsFor(store, existing, view, startWith)
  const errorLine = $('#ferr')
  errorLine.textContent = ''

  dialog.showModal()

  $('#cancel').onclick = () => dialog.close()

  const deleteButton = $<HTMLButtonElement>('#del')
  deleteButton.hidden = !id || store !== 'entries'
  deleteButton.onclick = () => {
    void api
      .del(`/api/transactions/${id}`)
      .then(() => {
        dialog.close()
        return onSaved()
      })
      .catch((problem: unknown) => {
        errorLine.textContent =
          problem instanceof ApiError ? problem.message : 'Could not delete.'
      })
  }

  form.onsubmit = (event) => {
    event.preventDefault()
    errorLine.textContent = ''

    const data = Object.fromEntries(new FormData(form).entries()) as Record<string, string>
    const { url, method, body } = requestFor(store, id, data)

    void (method === 'POST' ? api.post(url, body) : api.put(url, body))
      .then(() => {
        dialog.close()
        return onSaved()
      })
      .catch((problem: unknown) => {
        errorLine.textContent =
          problem instanceof ApiError ? problem.message : 'Could not save. Try again.'
      })
  }
}

function findExisting(store: Store, id: string, view: MonthView): Record<string, unknown> | null {
  if (!id) return null
  switch (store) {
    case 'bills':
      return (view.bills.find((bill) => bill.id === id) ?? null) as Record<string, unknown> | null
    case 'payroll':
      return (view.people.find((person) => person.id === id) ?? null) as Record<string, unknown> | null
    case 'income':
      return (view.incomeSources.find((source) => source.id === id) ?? null) as Record<string, unknown> | null
    case 'categories':
      return (view.categories.find((category) => category.id === id) ?? null) as Record<string, unknown> | null
    case 'entries':
      return (view.transactions.find((item) => item.id === id) ?? null) as Record<string, unknown> | null
    default:
      return null
  }
}

const field = (label: string, input: string) =>
  `<div class="field"><label>${esc(label)}</label>${input}</div>`

/** A note at the top of a form. The text is trusted markup, never user input. */
const explain = (html: string) => `<p class="dlg-explain">${html}</p>`

const text = (name: string, value: unknown, extra = '') =>
  `<input name="${name}" value="${esc(value ?? '')}" ${extra}>`

function options(list: Array<{ value: string; label: string }>, selected: unknown): string {
  return list
    .map(
      (option) =>
        `<option value="${esc(option.value)}" ${option.value === selected ? 'selected' : ''}>${esc(
          option.label,
        )}</option>`,
    )
    .join('')
}

function fieldsFor(
  store: Store,
  existing: Record<string, unknown> | null,
  view: MonthView,
  prefill: Prefill,
): string {
  const categoryOptions = [
    { value: '', label: 'No category' },
    ...view.categories.map((category) => ({ value: category.id, label: category.name })),
  ]

  // With no categories yet the dropdown offers only "No category", which reads
  // as a broken control rather than an empty list. Say what is going on, and
  // that leaving it blank is a perfectly good answer for now.
  const noCategoriesYet =
    view.categories.length === 0
      ? explain(
          'You have no spending categories yet, so there is nothing to choose here. ' +
            'Add them under <b>Budget by category</b> — or leave this as ' +
            '<b>No category</b> and come back to set it once they exist.',
        )
      : ''

  switch (store) {
    case 'categories':
      return (
        field('Name', text('name', existing?.['name'], 'required')) +
        field('Monthly budget', text('monthlyBudget', existing?.['monthlyBudget'] ?? '0.00')) +
        field(
          'Processing / card fees',
          `<select name="isProcessingFees">${options(
            [
              { value: 'false', label: 'No' },
              { value: 'true', label: 'Yes — report separately' },
            ],
            String(existing?.['isProcessingFees'] ?? false),
          )}</select>`,
        ) +
        actions()
      )

    case 'bills':
      return (
        noCategoriesYet +
        field('Name', text('name', existing?.['name'], 'required')) +
        field('Amount', text('amount', existing?.['amount'], 'required')) +
        field(
          'Due day',
          text('dueDay', existing?.['dueDay'] ?? 1, 'type="number" min="1" max="31" required'),
        ) +
        field('Category', `<select name="categoryId">${options(categoryOptions, existing?.['categoryId'] ?? '')}</select>`) +
        field(
          'Autopay',
          `<select name="autopay">${options(
            [
              { value: 'false', label: 'No' },
              { value: 'true', label: 'Yes' },
            ],
            String(existing?.['autopay'] ?? false),
          )}</select>`,
        ) +
        actions()
      )

    case 'payroll':
      return (
        field('Name', text('name', existing?.['name'], 'required')) +
        field(
          'Kind',
          `<select name="kind">${options(
            ['Employee', 'Owner pay', 'Contractor'].map((value) => ({ value, label: value })),
            existing?.['kind'] ?? 'Employee',
          )}</select>`,
        ) +
        field('Amount per payday', text('amount', existing?.['amount'], 'required')) +
        field(
          'Frequency',
          `<select name="frequency">${options(
            ['Weekly', 'Biweekly', 'Semi-monthly', 'Monthly'].map((value) => ({
              value,
              label: value,
            })),
            existing?.['frequency'] ?? 'Monthly',
          )}</select>`,
        ) +
        field('Anchor date', text('anchorDate', existing?.['anchorDate'], 'type="date"')) +
        actions()
      )

    case 'income':
      // An income source is a label, not an amount. People reasonably expect
      // to type a figure here, so the form says where the figure goes instead
      // of leaving them hunting for a field that does not exist.
      return (
        explain(
          'What this source brings in <b>in a normal month</b>. The Recurring ones ' +
            'add up to your Recurring Monthly Revenue, which is what the weekly card ' +
            'charge is worked out from. Actual deposits are recorded separately, under ' +
            '<b>Spending &amp; deposits</b>.',
        ) +
        field('Name', text('name', existing?.['name'], 'required')) +
        field(
          'Expected each month',
          text('expectedMonthly', existing?.['expectedMonthly'] ?? '0.00'),
        ) +
        field(
          'Revenue type',
          `<select name="revenueType">${options(
            ['Recurring', 'Additional'].map((value) => ({ value, label: value })),
            existing?.['revenueType'] ?? 'Recurring',
          )}</select>`,
        ) +
        field(
          'Usually paid by',
          `<select name="defaultPaymentMethod">${options(
            ['Card', 'ACH', 'Check', 'Other'].map((value) => ({ value, label: value })),
            existing?.['defaultPaymentMethod'] ?? 'Other',
          )}</select>`,
        ) +
        actions()
      )

    default: {
      const kind = String(existing?.['kind'] ?? prefill.kind ?? 'expense')
      const report = (existing?.['report'] ?? null) as Record<string, unknown> | null

      return (
        field(
          'Type',
          `<select name="kind" id="f-type">${options(
            [
              { value: 'expense', label: 'Spending' },
              { value: 'income', label: 'Deposit' },
              { value: 'transfer', label: 'Transfer between NI accounts' },
            ],
            kind,
          )}</select>`,
        ) +
        field(
          'Date',
          text(
            'occurredOn',
            existing?.['occurredOn'] ?? prefill.occurredOn ?? view.today,
            'type="date" required',
          ),
        ) +
        field('Amount', text('amount', existing?.['amount'], 'required')) +
        field('Description (as it appears on the statement)', text('description', existing?.['description'])) +
        (kind === 'expense'
          ? noCategoriesYet +
            field(
              'Category',
              `<select name="categoryId">${options(categoryOptions, existing?.['categoryId'] ?? '')}</select>`,
            ) +
            field('Who made it', text('buyer', report?.['buyer'])) +
            field('What was purchased', text('item', report?.['item'])) +
            field('Business purpose', text('purpose', report?.['purpose'])) +
            field('Trip', text('trip', report?.['trip'])) +
            field('Paid with', text('paidWith', report?.['paidWith']))
          : '') +
        (kind === 'income'
          ? field(
              'Revenue type',
              `<select name="revenueType">${options(
                ['Recurring', 'Additional'].map((value) => ({ value, label: value })),
                existing?.['revenueType'] ?? prefill.revenueType ?? 'Recurring',
              )}</select>`,
            ) +
            field(
              'Paid by',
              `<select name="paymentMethod">${options(
                ['Card', 'ACH', 'Check', 'Other'].map((value) => ({ value, label: value })),
                existing?.['paymentMethod'] ?? prefill.paymentMethod ?? 'Card',
              )}</select>`,
            ) +
            field(
              'Income source',
              `<select name="incomeSourceId">${options(
                [
                  { value: '', label: 'No source' },
                  ...view.incomeSources.map((source) => ({
                    value: source.id,
                    label: source.name,
                  })),
                ],
                existing?.['incomeSourceId'] ?? '',
              )}</select>`,
            )
          : '') +
        actions()
      )
    }
  }
}

function actions(): string {
  return `<p class="err" id="ferr" role="alert"></p>
  <div class="dlg-actions">
    <button class="btn" type="submit">Save</button>
    <button class="btn ghost" type="button" id="cancel">Cancel</button>
    <button class="btn danger" type="button" id="del">Delete</button>
  </div>`
}

/** Maps a form submission onto the right endpoint and payload shape. */
function requestFor(
  store: Store,
  id: string,
  data: Record<string, string>,
): { url: string; method: 'POST' | 'PUT'; body: Record<string, unknown> } {
  const blankToNull = (value: string | undefined) => (value === '' ? null : value)

  switch (store) {
    case 'categories':
      return {
        url: id ? `/api/categories/${id}` : '/api/categories',
        method: id ? 'PUT' : 'POST',
        body: {
          name: data['name'],
          monthlyBudget: data['monthlyBudget'] || '0',
          isProcessingFees: data['isProcessingFees'] === 'true',
        },
      }

    case 'bills':
      return {
        url: id ? `/api/bills/${id}` : '/api/bills',
        method: id ? 'PUT' : 'POST',
        body: {
          name: data['name'],
          amount: data['amount'],
          dueDay: Number(data['dueDay']),
          categoryId: blankToNull(data['categoryId']),
          autopay: data['autopay'] === 'true',
        },
      }

    case 'payroll':
      return {
        url: id ? `/api/payroll/${id}` : '/api/payroll',
        method: id ? 'PUT' : 'POST',
        body: {
          name: data['name'],
          kind: data['kind'],
          amount: data['amount'],
          frequency: data['frequency'],
          anchorDate: blankToNull(data['anchorDate']),
        },
      }

    case 'income':
      return {
        url: id ? `/api/income-sources/${id}` : '/api/income-sources',
        method: id ? 'PUT' : 'POST',
        body: {
          name: data['name'],
          revenueType: data['revenueType'],
          defaultPaymentMethod: data['defaultPaymentMethod'],
          expectedMonthly: data['expectedMonthly'] || '0',
        },
      }

    default: {
      const isExpense = data['kind'] === 'expense'
      const hasReport = Boolean(
        data['buyer'] || data['item'] || data['purpose'] || data['trip'] || data['paidWith'],
      )

      return {
        url: id ? `/api/transactions/${id}` : '/api/transactions',
        method: id ? 'PUT' : 'POST',
        body: {
          occurredOn: data['occurredOn'],
          kind: data['kind'],
          amount: data['amount'],
          description: data['description'] ?? '',
          ...(isExpense ? { categoryId: blankToNull(data['categoryId']) } : {}),
          ...(data['kind'] === 'income'
            ? {
                revenueType: data['revenueType'],
                paymentMethod: data['paymentMethod'],
                incomeSourceId: blankToNull(data['incomeSourceId']),
              }
            : {}),
          ...(isExpense && hasReport
            ? {
                report: {
                  buyer: blankToNull(data['buyer']),
                  item: blankToNull(data['item']),
                  purpose: blankToNull(data['purpose']),
                  trip: blankToNull(data['trip']),
                  paidWith: blankToNull(data['paidWith']),
                },
              }
            : {}),
        },
      }
    }
  }
}
